import React, { useState, useEffect } from 'react';
import AddDrinkModal from './AddDrinkModal';
import EditDrinkModal from './EditDrinkModal';
import InviteMemberModal from './InviteMemberModal';
import { iconToEmoji } from '../utils/iconUtils';
import { apiService } from '../services/api';

const AdminPage = ({ user, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [members, setMembers] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  // const [icons, setIcons] = useState([]); // Unused variable
  const [showAddDrinkModal, setShowAddDrinkModal] = useState(false);
  const [showEditDrinkModal, setShowEditDrinkModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedDrink, setSelectedDrink] = useState(null);
  const [isSortingMode, setIsSortingMode] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: '📊' },
    { id: 'members', label: 'Mitglieder', icon: '👥' },
    { id: 'drinks', label: 'Getränke', icon: '🍺' },
    { id: 'transactions', label: 'Transaktionen', icon: '📜' }
  ];

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const [drinksRes, membersRes, transactionsRes] = await Promise.all([
        apiService.getDrinks(),
        apiService.getMembers(),
        apiService.getAllTransactions(),
      ]);

      if (drinksRes.status === 200) {
        setDrinks(drinksRes.data.drinks || []);
      }
      if (membersRes.status === 200) {
        setMembers(membersRes.data.members || []);
      }
      if (transactionsRes.status === 200) {
        setTransactions(transactionsRes.data.transactions || []);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Admin-Daten:', error);
      setMessage('Fehler beim Laden der Daten');
    }
    setLoading(false);
  };


  const updateMemberBalance = async (memberId, newBalance) => {
    setLoading(true);
    try {
      await apiService.updateMemberBalance(memberId, newBalance);
      setMessage('Guthaben erfolgreich aktualisiert!');
      loadAdminData();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Guthabens:', error);
      setMessage('Fehler beim Aktualisieren des Guthabens');
    }
    setLoading(false);
  };

  const resetPassword = async (memberId) => {
    if (!window.confirm('Möchtest du das Passwort wirklich zurücksetzen?')) return;
    
    setLoading(true);
    try {
      const response = await apiService.resetPassword(memberId);
      setMessage(`Passwort zurückgesetzt! Neues Passwort: ${response.data.new_password}`);
    } catch (error) {
      console.error('Fehler beim Zurücksetzen des Passworts:', error);
      setMessage('Fehler beim Zurücksetzen des Passworts');
    }
    setLoading(false);
  };

  const deleteMember = async (memberId) => {
    if (!window.confirm('Möchtest du dieses Mitglied wirklich löschen?')) return;
    
    setLoading(true);
    try {
      await apiService.deleteMember(memberId);
      setMessage('Mitglied erfolgreich gelöscht!');
      loadAdminData();
    } catch (error) {
      console.error('Fehler beim Löschen des Mitglieds:', error);
      setMessage('Fehler beim Löschen des Mitglieds');
    }
    setLoading(false);
  };

  // eslint-disable-next-line no-unused-vars
  const updateDrinkPrice = async (drinkId, newPrice) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/drinks/${drinkId}/update-price/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price: newPrice }),
        credentials: 'include'
      });
      
      if (response.ok) {
        setMessage('Preis erfolgreich aktualisiert!');
        loadAdminData();
      } else {
        const errorData = await response.json();
        setMessage(`Fehler: ${errorData.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Preises:', error);
      setMessage('Fehler beim Aktualisieren des Preises');
    }
    setLoading(false);
  };

  // eslint-disable-next-line no-unused-vars
  const updateDrinkIcon = async (drinkId, newIcon) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/drinks/${drinkId}/update-icon/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ icon: newIcon }),
        credentials: 'include'
      });
      
      if (response.ok) {
        setMessage('Icon erfolgreich aktualisiert!');
        loadAdminData();
      } else {
        const errorData = await response.json();
        setMessage(`Fehler: ${errorData.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Icons:', error);
      setMessage('Fehler beim Aktualisieren des Icons');
    }
    setLoading(false);
  };

  const addDrink = async (name, price, icon, sortOrder = null) => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/drinks/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name,
          price: price,
          icon: icon,
          sort_order: sortOrder
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage(data.message || 'Getränk erfolgreich erstellt!');
        loadAdminData();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unbekannter Fehler');
      }
    } catch (error) {
      console.error('Fehler beim Erstellen des Getränks:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const editDrink = async (drinkId, name, price, icon) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/drinks/${drinkId}/update/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name,
          price: price,
          icon: icon
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage(data.message || 'Getränk erfolgreich bearbeitet!');
        loadAdminData();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unbekannter Fehler');
      }
    } catch (error) {
      console.error('Fehler beim Bearbeiten des Getränks:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleEditDrink = (drink) => {
    setSelectedDrink(drink);
    setShowEditDrinkModal(true);
  };

  const deleteDrink = async (drinkId) => {
    if (!window.confirm('Möchtest du dieses Getränk wirklich löschen?')) return;
    
    setLoading(true);
    try {
      await apiService.deleteDrink(drinkId);
      setMessage('Getränk erfolgreich gelöscht!');
      loadAdminData();
    } catch (error) {
      console.error('Fehler beim Löschen des Getränks:', error);
      setMessage('Fehler beim Löschen des Getränks');
    }
    setLoading(false);
  };

  const handleSortDrinks = async () => {
    if (!isSortingMode) {
      setIsSortingMode(true);
      return;
    }

    try {
      // Sortierreihenfolge basierend auf aktueller Reihenfolge erstellen
      const drinkOrders = drinks.map((drink, index) => ({
        id: drink.id,
        sort_order: index + 1
      }));

      await apiService.updateDrinkSortOrder(drinkOrders);
      setMessage('Sortierung erfolgreich gespeichert');
      setIsSortingMode(false);
      loadAdminData();
    } catch (error) {
      setMessage('Fehler beim Speichern der Sortierung: ' + (error.response?.data?.error || error.message));
    }
  };

  const moveDrink = (index, direction) => {
    if (!isSortingMode) return;
    
    const newDrinks = [...drinks];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < newDrinks.length) {
      [newDrinks[index], newDrinks[newIndex]] = [newDrinks[newIndex], newDrinks[index]];
      setDrinks(newDrinks);
    }
  };

  const cancelTransaction = async (transactionId) => {
    if (!window.confirm('Möchtest du diese Transaktion wirklich stornieren?')) return;
    
    setLoading(true);
    try {
      await apiService.cancelTransaction(transactionId);
      setMessage('Transaktion erfolgreich storniert!');
      loadAdminData();
    } catch (error) {
      console.error('Fehler beim Stornieren der Transaktion:', error);
      setMessage('Fehler beim Stornieren der Transaktion');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-bg-1">
      {/* Header */}
      <div className="bg-bg-2 border-b border-accent-2 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="text-text-subtle hover:text-text-normal transition-colors"
              >
                <span className="text-2xl">←</span>
              </button>
              <h1 className="text-2xl font-bold text-text-strong">Admin Bereich</h1>
            </div>
                          <div className="text-sm text-gray-700">
                Angemeldet als: {user?.username}
              </div>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className="max-w-4xl mx-auto px-4 mt-4">
          <div className="bg-accent-1/10 border border-accent-1/30 rounded-lg p-3 text-sm text-text-strong">
            {message}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-bg-2 border-b border-accent-2">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-accent-1 border-accent-1 bg-accent-1/10'
                    : 'text-text-normal border-transparent hover:text-accent-1 hover:bg-accent-1/5'
                }`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-1"></div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && !loading && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-6 rounded-lg border border-accent-2 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Mitglieder</h3>
                <p className="text-3xl font-bold text-accent-1">{Array.isArray(members) ? members.length : 0}</p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-accent-2 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Getränke</h3>
                <p className="text-3xl font-bold text-accent-1">{Array.isArray(drinks) ? drinks.length : 0}</p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-accent-2 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Transaktionen</h3>
                <p className="text-3xl font-bold text-accent-1">{Array.isArray(transactions) ? transactions.length : 0}</p>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-accent-2 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Schnellaktionen</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="btn-primary py-3"
                >
                  📧 Einladungslink erstellen
                </button>
                <button
                  onClick={() => setActiveTab('members')}
                  className="btn-secondary py-3"
                >
                  👥 Mitglieder verwalten
                </button>
                <button
                  onClick={() => setActiveTab('drinks')}
                  className="btn-secondary py-3"
                >
                  🍺 Getränke verwalten
                </button>
                <button
                  onClick={() => setActiveTab('transactions')}
                  className="btn-secondary py-3"
                >
                  📜 Transaktionen anzeigen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && !loading && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">Mitglieder verwalten</h3>
              <button
                onClick={() => setShowInviteModal(true)}
                className="btn-primary"
              >
                📧 Neues Mitglied einladen
              </button>
            </div>
            

            <div className="space-y-4">
              {Array.isArray(members) && members.map(member => (
                <div key={member.id} className="bg-white p-6 rounded-lg border border-accent-2 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                                            <h4 className="font-semibold text-gray-900 text-lg">
                        {member.user_first_name ? `${member.user_first_name} (${member.user_username})` : member.user_username}
                      </h4>
                      <p className="text-sm text-gray-700">{member.user_email}</p>
                      <p className="text-sm text-gray-700">Guthaben: {member.balance}€</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          const newBalance = window.prompt('Neues Guthaben:', member.balance);
                          if (newBalance !== null) {
                            updateMemberBalance(member.id, parseFloat(newBalance));
                          }
                        }}
                        className="btn-secondary text-sm px-4 py-2"
                      >
                        💰 Guthaben
                      </button>
                      <button
                        onClick={() => resetPassword(member.id)}
                        className="btn-secondary text-sm px-4 py-2"
                      >
                        🔑 Passwort
                      </button>
                      <button
                        onClick={() => deleteMember(member.id)}
                        className="btn-danger text-sm px-4 py-2"
                      >
                        🗑️ Löschen
                      </button>
                    </div>
                  </div>
                </div>
              ))}
                            {!Array.isArray(members) && (
                <div className="text-center py-8">
                  <p className="text-gray-700">Lade Mitglieder...</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Drinks Tab */}
        {activeTab === 'drinks' && !loading && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">Getränke verwalten</h3>
              <div className="flex gap-2">
                <button
                  onClick={handleSortDrinks}
                  className={`btn-secondary ${isSortingMode ? 'bg-accent-1 text-white' : ''}`}
                >
                  {isSortingMode ? '💾 Sortierung speichern' : '🔄 Sortierung bearbeiten'}
                </button>
                <button
                  onClick={() => setShowAddDrinkModal(true)}
                  className="btn-primary"
                >
                  ➕ Neues Getränk
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {Array.isArray(drinks) && drinks.map((drink, index) => (
                <div key={drink.id} className="bg-white p-6 rounded-lg border border-accent-2 shadow-sm">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      {isSortingMode && (
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => moveDrink(index, 'up')}
                            disabled={index === 0}
                            className="btn-secondary text-xs px-2 py-1 disabled:opacity-50"
                          >
                            ⬆️
                          </button>
                          <button
                            onClick={() => moveDrink(index, 'down')}
                            disabled={index === drinks.length - 1}
                            className="btn-secondary text-xs px-2 py-1 disabled:opacity-50"
                          >
                            ⬇️
                          </button>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <img 
                            src={`/icons/drinks/${drink.icon}`} 
                            alt={drink.name} 
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              // Fallback auf Emoji wenn PNG nicht gefunden
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <span className="text-xl hidden">{iconToEmoji(drink.icon)}</span>
                          <h4 className="font-semibold text-gray-900 text-lg">{drink.name}</h4>
                          {isSortingMode && (
                            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              #{index + 1}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700">{drink.price}€</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!isSortingMode && (
                        <>
                          <button
                            onClick={() => handleEditDrink(drink)}
                            className="btn-secondary text-sm px-4 py-2"
                          >
                            ✏️ Bearbeiten
                          </button>
                          <button
                            onClick={() => deleteDrink(drink.id)}
                            className="btn-danger text-sm px-4 py-2"
                          >
                            🗑️ Löschen
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {!Array.isArray(drinks) && (
                <div className="text-center py-8">
                  <p className="text-gray-700">Lade Getränke...</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && !loading && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">Transaktionen verwalten</h3>
            </div>
            
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {Array.isArray(transactions) && transactions.map(transaction => (
                <div key={transaction.id} className="bg-white p-6 rounded-lg border border-accent-2 shadow-sm">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900 text-lg">
                          {transaction.type === 'purchase' ? '🍺 ' : '💰 '}
                          {transaction.description || (transaction.drink_name ? `${transaction.drink_name} gekauft` : 'Transaktion')}
                        </h4>
                        <span className={`text-sm font-medium px-2 py-1 rounded ${
                          transaction.amount < 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {transaction.amount > 0 ? '+' : ''}{transaction.amount}€
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-700">
                        <p>
                          <span className="font-medium">Von:</span> {transaction.member_name || 'Unbekannt'} 
                          {transaction.member_username && ` (@${transaction.member_username})`}
                        </p>
                        
                        {transaction.type === 'purchase' && transaction.paid_by_name && transaction.paid_by_name !== transaction.member_name && (
                          <p>
                            <span className="font-medium">Bezahlt von:</span> {transaction.paid_by_name}
                            {transaction.paid_by_username && ` (@${transaction.paid_by_username})`}
                          </p>
                        )}
                        
                        {transaction.gift_giver_name && (
                          <p className="text-accent-1">
                            🎁 Geschenk von: {transaction.gift_giver_name} 
                            {transaction.gift_type === 'next_round' ? ' (Nächste Runde)' : ' (Ganzer Abend)'}
                            {transaction.gift_message && ` - "${transaction.gift_message}"`}
                          </p>
                        )}
                        
                        <p className="text-xs text-gray-600">
                          📅 {new Date(transaction.timestamp).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => cancelTransaction(transaction.id)}
                      className="btn-danger text-sm px-4 py-2 flex-shrink-0"
                    >
                      ❌ Stornieren
                    </button>
                  </div>
                </div>
              ))}
              {!Array.isArray(transactions) && (
                <div className="text-center py-8">
                  <p className="text-gray-700">Lade Transaktionen...</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Drink Modal */}
                  <AddDrinkModal
              isOpen={showAddDrinkModal}
              onClose={() => setShowAddDrinkModal(false)}
              onAddDrink={addDrink}
            />
            <EditDrinkModal
              isOpen={showEditDrinkModal}
              onClose={() => setShowEditDrinkModal(false)}
              onEditDrink={editDrink}
              drink={selectedDrink}
            />
            <InviteMemberModal
              isOpen={showInviteModal}
              onClose={() => setShowInviteModal(false)}
            />
    </div>
  );
};

export default AdminPage;
